import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, TrendingUp, MessageSquare, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  cleanliness: 'Sauberkeit',
  training_quality: 'Trainingsqualität',
  opening_hours: 'Trainingszeiten',
  equipment: 'Geräte & Ausstattung',
  staff_friendliness: 'Team-Freundlichkeit',
  value_for_money: 'Preis-Leistung',
};

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-1 font-semibold" data-testid="star-value">
    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
    {value ?? '—'}
  </span>
);

export default function FeedbackAnalyticsPage() {
  const [data, setData] = useState(null);
  const [googleUrl, setGoogleUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/admin/feedback/analytics`, { headers })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Auswertung konnte nicht geladen werden'));
    axios.get(`${API}/admin/feedback/google-review-settings`, { headers })
      .then((r) => setGoogleUrl(r.data.url || ''))
      .catch(() => {});
  }, []);

  const saveGoogleUrl = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/feedback/google-review-settings`, { url: googleUrl }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Google-Bewertungslink gespeichert');
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return <div className="p-8 text-muted-foreground" data-testid="feedback-analytics-loading">Lade Auswertung…</div>;
  }

  const fb = data.appointment_feedback;
  const sv = data.surveys;
  const gr = data.google_reviews;

  return (
    <div className="p-6 space-y-6" data-testid="feedback-analytics-page">
      <div>
        <h1 className="text-2xl font-bold">Bewertungen & Umfragen</h1>
        <p className="text-muted-foreground text-sm">Termin-Feedback, Quartals-Umfragen und Google-Bewertungen</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-avg-rating">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Ø Training-Bewertung</CardTitle></CardHeader>
          <CardContent className="text-2xl font-bold"><Stars value={fb.avg_rating} /> <span className="text-sm text-muted-foreground font-normal">({fb.total})</span></CardContent>
        </Card>
        <Card data-testid="kpi-avg-trainer">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Ø Trainer-Bewertung</CardTitle></CardHeader>
          <CardContent className="text-2xl font-bold"><Stars value={fb.avg_trainer_rating} /></CardContent>
        </Card>
        <Card data-testid="kpi-nps">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">NPS (Weiterempfehlung)</CardTitle></CardHeader>
          <CardContent className="text-2xl font-bold">{sv.nps ?? '—'} <span className="text-sm text-muted-foreground font-normal">({sv.nps_responses} Antworten)</span></CardContent>
        </Card>
        <Card data-testid="kpi-google">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Google-Anfragen</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <div>Gefragt: <b>{gr.asked}</b></div>
            <div>Geöffnet: <b>{gr.opened}</b> · Schon bewertet: <b>{gr.already_done}</b></div>
          </CardContent>
        </Card>
      </div>

      {/* Verlauf */}
      {fb.monthly_trend.length > 0 && (
        <Card data-testid="trend-chart">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Ø-Bewertung Verlauf (12 Monate)</CardTitle></CardHeader>
          <CardContent style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={fb.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis domain={[1, 5]} fontSize={12} />
                <Tooltip />
                <Line type="monotone" dataKey="avg_rating" name="Ø Bewertung" stroke="#eab308" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Pro Trainer */}
        <Card data-testid="trainer-table">
          <CardHeader><CardTitle className="text-base">Pro Trainer</CardTitle></CardHeader>
          <CardContent>
            {fb.by_trainer.length === 0 ? <p className="text-sm text-muted-foreground">Noch keine Bewertungen.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b"><th className="py-1">Trainer</th><th>Anzahl</th><th>Ø Termin</th><th>Ø Trainer</th></tr></thead>
                <tbody>
                  {fb.by_trainer.map((t) => (
                    <tr key={t.trainer_id} className="border-b last:border-0">
                      <td className="py-2">{t.name}</td><td>{t.count}</td>
                      <td><Stars value={t.avg_rating} /></td>
                      <td>{t.avg_trainer_rating != null ? <Stars value={t.avg_trainer_rating} /> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Pro Kurs */}
        <Card data-testid="service-table">
          <CardHeader><CardTitle className="text-base">Pro Kurs / Leistung</CardTitle></CardHeader>
          <CardContent>
            {fb.by_service.length === 0 ? <p className="text-sm text-muted-foreground">Noch keine Bewertungen.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b"><th className="py-1">Kurs</th><th>Anzahl</th><th>Ø</th></tr></thead>
                <tbody>
                  {fb.by_service.map((s) => (
                    <tr key={s.service_id} className="border-b last:border-0">
                      <td className="py-2">{s.name}</td><td>{s.count}</td><td><Stars value={s.avg_rating} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quartals-Umfrage */}
      <Card data-testid="survey-categories">
        <CardHeader><CardTitle className="text-base">Quartals-Umfrage — Ø pro Kategorie ({sv.total} Teilnahmen)</CardTitle></CardHeader>
        <CardContent>
          {Object.keys(sv.categories).length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Umfrage-Antworten. Die Umfrage erscheint automatisch alle 3 Monate in der Kunden-App.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(sv.categories).map(([qid, c]) => (
                <div key={qid} className="rounded-lg border p-3">
                  <div className="text-sm text-muted-foreground">{CATEGORY_LABELS[qid] || qid}</div>
                  <div className="text-xl font-bold"><Stars value={c.avg} /> <span className="text-xs text-muted-foreground font-normal">({c.count})</span></div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kommentare */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card data-testid="feedback-comments">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Letzte Training-Kommentare</CardTitle></CardHeader>
          <CardContent className="space-y-3 max-h-80 overflow-y-auto">
            {fb.recent_comments.length === 0 ? <p className="text-sm text-muted-foreground">Keine Kommentare.</p> :
              fb.recent_comments.map((c) => (
                <div key={c.id} className="text-sm border-b pb-2 last:border-0">
                  <div className="flex justify-between"><b>{c.customer_name || 'Kunde'}</b><Stars value={c.rating} /></div>
                  <p className="text-muted-foreground">{c.comment}</p>
                  <span className="text-xs text-muted-foreground">{(c.created_at || '').slice(0, 10)}</span>
                </div>
              ))}
          </CardContent>
        </Card>
        <Card data-testid="survey-comments">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Verbesserungsvorschläge (Umfrage)</CardTitle></CardHeader>
          <CardContent className="space-y-3 max-h-80 overflow-y-auto">
            {sv.recent_comments.length === 0 ? <p className="text-sm text-muted-foreground">Keine Kommentare.</p> :
              sv.recent_comments.map((c) => (
                <div key={c.id} className="text-sm border-b pb-2 last:border-0">
                  <b>{c.customer_name || 'Kunde'}</b>
                  <p className="text-muted-foreground">{c.comment}</p>
                  <span className="text-xs text-muted-foreground">{(c.created_at || '').slice(0, 10)}</span>
                </div>
              ))}
          </CardContent>
        </Card>
      </div>

      {/* Google Review Settings */}
      <Card data-testid="google-review-settings">
        <CardHeader><CardTitle className="text-base">Google-Bewertungslink</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Kunden werden 1x pro Jahr in der App gebeten, eine Google-Bewertung abzugeben. Ohne Link erscheint keine Anfrage.
            Link findest du unter business.google.com → «Mehr Rezensionen erhalten».
          </p>
          <div className="flex gap-2">
            <Input
              data-testid="google-review-url-input"
              placeholder="https://g.page/r/…/review"
              value={googleUrl}
              onChange={(e) => setGoogleUrl(e.target.value)}
            />
            <Button onClick={saveGoogleUrl} disabled={saving} data-testid="google-review-url-save">
              <Save className="w-4 h-4 mr-1" /> Speichern
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
